import _ from "underscore";
import React from "react";
import Cookies from "universal-cookie";
import configureStore from "configure-store";
import HistoryState from "history-state";
import { createRoot } from "react-dom/client";
import getFlagsFromObj from "helpers/get-flags-from-obj";

import OnInitializationActions, { getCsrfToken } from "actions/on-initialization-actions";
import OneTimeExperienceActions from "actions/one-time-experience-actions";
import ReduxApp from "redux-app";
import { connect, Provider } from "react-redux";
import { CookiesProvider } from "react-cookie";
import { BrowserProvider } from "../components/layout/BrowserContext";
import { applyToServersideComponents } from "helpers/react-hypernova-helper";
import { initLocationChangeEvent } from "../components/source/hoc/with-query-params";
import { URLProvider } from "components/layout/URLContext";
import { queryStringDecode } from "./query-string-decoder-helper";
import setupNavigation from "./setup-navigation";
import { userDataSelector } from "../components/source/hoc/with-user-data";
import UserProfileActions from "../actions/userProfile-actions";
import setupAnalyticsIntegrations from "./setup-analytics-integrations";
import { selectFeatureFlagEnabled, selectFeatureFlags } from "../selectors/featureFlagSelectors";
import { initSailthru } from "./third-party/sailthru-helpers";
import { checkIfTouchDevice } from "helpers/device-helpers";
import { sendGlobalInferLog } from "../analytics/global-infer-log";
import { cleanUpLocalStorageOnLogout } from "../helpers/logout-helpers";
import { incrementPagesViewed } from "../helpers/branch-banner-helpers";
import { registerStorageListenerForAuthChanges } from "./auth-helpers";
import ProductsActions from "actions/products-actions";
import { initializeStoreForAjaxHelper } from "./ajax-helpers";

const ReactReduxInitializer = {
  isInitialized: false,
  store: null,

  // BFCache (Back/Forward Cache) is the Browsers internal HTML store. it kicks
  // in when you use the Back/Forward buttons. Instead of making a request to
  // the server, it tries to render what it has in cache (dishonoring all cache
  // headers btw!) BFCache updates when the URL changes*, which is typically on
  // page load, but it doesn't have to be.
  //
  // * - as you might expect, implementations vary. This mostly describes Safari
  //
  // Use this method when you client-side render and want to be sure that when
  // a user hits the back button, they get the most recent rendering of a page,
  // not the one that was present on page load.
  // you typically call this method using the React life cycle method
  //
  // ``` React
  // componentDidUpdate(prevProps, prevState) {
  //   if (/* props data which impact the markup are unchanged */) { return; }
  //   if (/* state data which impact the markup are unchanged */) { return; }
  //   ReactReduxInitializer.resetBFCache();
  // }
  // ```
  resetBFCache() {
    // use a randomized URL to allow for multiple calls to resetBFCache on a
    // single page. (without it, the URL would not update on subsequent calls)
    const randomToken = new Date().getTime();
    const historyState = new HistoryState();

    // TODO: consider any existing hash
    historyState.replaceState(null, null, `#${randomToken}`);
  },

  async mountApp(initialState) {
    if (this.isInitialized) {
      window.console?.warn("App already mounted: ReactReduxInitializer.mountApp called more than once.");
      return;
    } else {
      this.isInitialized = true;
    }

    this.store = configureStore(ReduxApp, initialState);
    window.globalStoreOnlyToUseForDebugging = this.store;
    initializeStoreForAjaxHelper(this.store);

    this.store.dispatch(getCsrfToken());

    registerStorageListenerForAuthChanges();

    // gMapsLoaded callback
    // This callback is called when the Google Maps script loads. Until this point, we're not able to render
    // an autocomplete component. This function is called when that library is loaded and passes an event to
    // react which calls setState.
    window.gMapsLoaded = () => {
      this.store.dispatch(OnInitializationActions.googleMapsLoaded());
    };
    // gMapsLoaded callback.

    // This is consumed within ux-user.js
    if (window.RTR?.UX?.isInitialized) {
      //set userData
      this.store.dispatch(OnInitializationActions.setUxUserData());

      // set the enabled flags client side
      const { flags } = getFlagsFromObj(initialState).enabledFlags;
      this.store.dispatch(OnInitializationActions.setFlags(flags));

      if (selectFeatureFlagEnabled("tatari")(initialState)) {
        this.store.dispatch(OnInitializationActions.initTatari());
      }

      const userData = userDataSelector(initialState);

      this.store.dispatch(ProductsActions.setMembershipLensOnPageLoad(userData));

      const existingQuery = {
        queryParams: queryStringDecode(window.location.search.substring(1)),
        isGrid: typeof initialState?.gridView === "object",
        isPDP: typeof initialState?.product === "object",
      };

      this.store.dispatch(OnInitializationActions.initializeFilters(existingQuery));

      this.store.dispatch(UserProfileActions.fetchUserReviewSummary());
    }

    this.store.dispatch(OnInitializationActions.fireRubyNextTrackingEvents("ruby"));

    OneTimeExperienceActions.getAllOneTimeExperienceStatuses().forEach(([experienceName, status]) => {
      this.store.dispatch(
        OneTimeExperienceActions.setOneTimeExperienceDismissed({
          experienceName,
          dismissed: status,
        })
      );
    });
    setupNavigation(this.store);

    setupAnalyticsIntegrations(this.store);

    // URL change handling
    initLocationChangeEvent();

    // send the previously written infer log to pixel
    sendGlobalInferLog();

    // add touch classnames to the document element
    checkIfTouchDevice();

    // increment the `pagesViewed` cookie - used for Branch IO integration
    incrementPagesViewed();

    // clear select local storage keys if user just logged out
    cleanUpLocalStorageOnLogout();

    // sailthru consent management
    initSailthru(selectFeatureFlags(initialState));

    // Hook up server-side rendered components to Redux
    applyToServersideComponents((node, componentClass) => {
      // provides _everything_ in the store to the component... not a decision
      // I would make if I were restarting this project.
      const ConnectedComponent = connect(_.identity)(componentClass);
      const PageComponent = (
        <Provider store={this.store}>
          <BrowserProvider>
            <URLProvider>
              <CookiesProvider cookies={new Cookies("")}>
                <ConnectedComponent />
              </CookiesProvider>
            </URLProvider>
          </BrowserProvider>
        </Provider>
      );

      createRoot(node).render(PageComponent);
    });
  },

  // This should probably be in the `ReactInitializer` module...but I secretly
  // hope we can remove it soon anyway.
  mountIsolatedComponent(props, name, id) {
    const node = document.querySelector("[data-isolated-react-class='" + name + "-" + id + "']");
    const componentClass = window[name];
    if (!componentClass) {
      window.console?.warn("Component not found: " + name);
      return;
    }

    createRoot(node).render(React.createElement(componentClass, props));
  },
};

export default ReactReduxInitializer;

export const { isInitialized, resetBFCache, mountApp, mountIsolatedComponent } = ReactReduxInitializer;
